import React, { useState, useCallback } from 'react';
import {
  AlertTriangle, Shield, FileText, MessageCircle,
  Terminal, ChevronDown, ChevronRight, Database, GitBranch, CheckCircle, XCircle,
  ShieldAlert, FileWarning, Check, Copy,
  BookMarked, Download,
} from 'lucide-react';

function Badge({ mode }) {
  const cls = mode === 'contract_grounded' ? 'sa-badge-contract'
    : mode === 'general_guidance_clarify' ? 'sa-badge-guidance'
    : 'sa-badge-clarify';
  const label = mode === 'contract_grounded' ? 'CONTRACT'
    : mode === 'general_guidance_clarify' ? 'GENERAL GUIDANCE'
    : 'NEEDS DETAIL';
  return <span className={`sa-badge ${cls}`} data-testid="answer-mode-pill">{label}</span>;
}

/* -- Section -- */

function Section({ icon: Icon, title, accent, testId, children }) {
  if (!children) return null;
  return (
    <div className={`sa-section sa-accent-${accent}`} data-testid={testId}>
      <div className="sa-section-head">
        {Icon && <Icon size={14} className="sa-section-icon" />}
        <span className="sa-section-title">{title}</span>
      </div>
      <div className="sa-section-body">{children}</div>
    </div>
  );
}

/* -- Decision Log Panel (Admin) -- */

function LogRow({ label, value, accent }) {
  if (value === undefined || value === null) return null;
  const display = typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value);
  return (
    <div className="dl-row">
      <span className="dl-label">{label}</span>
      <span className={`dl-value ${accent ? `dl-accent-${accent}` : ''}`}>{display}</span>
    </div>
  );
}

function ChunkCard({ chunk, index }) {
  return (
    <div className="dl-chunk">
      <div className="dl-chunk-head">
        <Database size={11} />
        <span>Chunk {index + 1}</span>
        <span className="dl-chunk-score">match: {chunk.issue_match_score} | score: {chunk.retrieval_score}</span>
      </div>
      <div className="dl-chunk-body">
        <div className="dl-chunk-meta">
          <span>{chunk.source_title}</span>
          <span>{chunk.article_number}</span>
          <span>{chunk.section_number}</span>
          <span>Page {chunk.page}</span>
          <span className="dl-chunk-issue">{chunk.chunk_issue}</span>
        </div>
        <div className="dl-chunk-preview">{chunk.text_preview}</div>
      </div>
    </div>
  );
}

function DecisionLogPanel({ log }) {
  const [open, setOpen] = useState(false);
  if (!log) return null;

  const passIcon = log.validator_result === 'pass' || log.validator_result === 'pass_after_downgrade';

  return (
    <div className="dl-panel" data-testid="decision-log-panel">
      <button className="dl-toggle" onClick={() => setOpen(!open)} data-testid="decision-log-toggle">
        <Terminal size={13} />
        <span>Pipeline Log</span>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </button>

      {open && (
        <div className="dl-content">
          {/* Human-readable reason */}
          {log.human_readable_reason && (
            <div className="dl-reason" data-testid="decision-log-reason">
              <GitBranch size={13} />
              <span>{log.human_readable_reason}</span>
            </div>
          )}

          {/* Routing section */}
          <div className="dl-section">
            <div className="dl-section-label">ROUTING</div>
            <LogRow label="Question Issue" value={log.question_issue} />
            <LogRow label="All Issues" value={log.all_issues?.join(', ')} />
            <LogRow label="Vague Detected" value={log.vague_detected} accent={log.vague_detected ? 'amber' : 'green'} />
            <LogRow label="Common Question" value={log.common_question} />
            <LogRow label="Selected Mode" value={log.mode} accent={log.mode === 'contract_grounded' ? 'green' : log.mode === 'general_guidance_clarify' ? 'amber' : 'red'} />
            <LogRow label="Multi-Issue" value={log.multi_issue} accent={log.multi_issue ? 'amber' : null} />
          </div>

          {/* Validation section */}
          <div className="dl-section">
            <div className="dl-section-label">VALIDATION</div>
            <LogRow label="Source Valid" value={log.source_valid} accent={log.source_valid ? 'green' : 'red'} />
            <LogRow label="Top Chunk Issue" value={log.top_chunk_issue} />
            <LogRow label="Issue Match" value={log.issue_match} accent={log.issue_match >= 0.9 ? 'green' : log.issue_match >= 0.45 ? 'amber' : 'red'} />
            <LogRow label="Confidence" value={log.confidence} accent={log.confidence >= 0.75 ? 'green' : log.confidence >= 0.5 ? 'amber' : 'red'} />
            <LogRow label="Confirmed Exact Quote" value={log.confirmed_exact_quote} accent={log.confirmed_exact_quote ? 'green' : null} />
            <div className="dl-row">
              <span className="dl-label">Validator</span>
              <span className={`dl-value dl-validator-${passIcon ? 'pass' : 'fail'}`}>
                {passIcon ? <CheckCircle size={12} /> : <XCircle size={12} />}
                {log.validator_result}
              </span>
            </div>
            <LogRow label="Sanitizer Applied" value={log.sanitizer_applied} />
          </div>

          {/* Block reason */}
          {log.contract_mode_block_reason && (
            <div className="dl-section">
              <div className="dl-section-label">CONTRACT MODE BLOCK</div>
              <div className="dl-block-reason">{log.contract_mode_block_reason}</div>
            </div>
          )}

          {/* Downgrade */}
          {log.downgraded && (
            <div className="dl-section">
              <div className="dl-section-label">DOWNGRADE</div>
              <LogRow label="Downgraded" value={log.downgraded} accent="amber" />
              <LogRow label="Reason" value={log.downgrade_reason} />
            </div>
          )}

          {/* Top Chunks */}
          {log.top_chunks?.length > 0 && (
            <div className="dl-section">
              <div className="dl-section-label">TOP CHUNKS ({log.chunk_count} total)</div>
              {log.top_chunks.map((chunk, i) => (
                <ChunkCard key={i} chunk={chunk} index={i} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* -- Copy Button -- */

function CopyButton({ text, label, testId }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [text]);
  return (
    <button className="stk-copy-btn" onClick={handleCopy} data-testid={testId}>
      {copied ? <Check size={13} /> : <Copy size={13} />}
      <span>{copied ? 'Copied' : label}</span>
    </button>
  );
}

/* -- Weingarten Rights Card -- */

function WeingartenCard({ card }) {
  if (!card) return null;
  return (
    <div className="stk-weingarten" data-testid="weingarten-card">
      <div className="stk-card-head stk-head-red">
        <ShieldAlert size={14} />
        <span>WEINGARTEN RIGHTS</span>
      </div>
      <div className="stk-card-body">
        <blockquote className="stk-statement" data-testid="weingarten-statement">
          {card.statement}
        </blockquote>
        <div className="stk-instruction">{card.instruction}</div>
        <div className="stk-trigger-reason" data-testid="weingarten-trigger-reason">
          Trigger: {card.trigger_reason}
        </div>
        <CopyButton
          text={card.statement}
          label="Copy Weingarten Statement"
          testId="copy-weingarten-btn"
        />
      </div>
    </div>
  );
}

/* -- Grievance Draft Card -- */

function GrievanceDraftCard({ draft }) {
  const [expanded, setExpanded] = useState(false);
  if (!draft) return null;

  const refConfirmed = draft.contract_reference_status === 'confirmed';

  return (
    <div className="stk-grievance" data-testid="grievance-draft-card">
      <div className="stk-card-head stk-head-amber">
        <FileWarning size={14} />
        <span>DRAFT GRIEVANCE</span>
        <span className="stk-draft-badge" data-testid="grievance-draft-badge">DRAFT</span>
      </div>
      <div className="stk-card-body">
        <div className="stk-draft-notice" data-testid="grievance-status-label">
          {draft.status_label}
        </div>

        {/* Violation */}
        {draft.violation_description && (
          <div className="stk-field">
            <div className="stk-field-label">Violation</div>
            <div className="stk-field-value">{draft.violation_description}</div>
          </div>
        )}

        {/* Contract reference */}
        <div className="stk-field">
          <div className="stk-field-label">Contract Reference</div>
          <div className={`stk-ref ${refConfirmed ? 'stk-ref-confirmed' : 'stk-ref-unconfirmed'}`} data-testid="contract-reference-status">
            {refConfirmed ? (
              <>
                <CheckCircle size={12} />
                <span>Confirmed contract reference</span>
              </>
            ) : (
              <>
                <AlertTriangle size={12} />
                <span>Agreement reference not confirmed</span>
              </>
            )}
          </div>
          <div className="stk-ref-text">{draft.contract_reference}</div>
        </div>

        {/* Remedies */}
        {draft.remedies?.length > 0 && (
          <div className="stk-field">
            <div className="stk-field-label">Remedies Requested</div>
            <ul className="stk-remedies" data-testid="grievance-remedies-list">
              {draft.remedies.map((r, i) => (
                <li key={i} className="stk-remedy-item">{r}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Fields needing input */}
        {draft.fields_needing_input?.length > 0 && (
          <div className="stk-field">
            <div className="stk-field-label">Fields You Need to Fill In</div>
            <div className="stk-fields-list">
              {draft.fields_needing_input.map((f, i) => (
                <span key={i} className="stk-field-tag">{f}</span>
              ))}
            </div>
          </div>
        )}

        {/* Filing note */}
        {draft.deadline_note && (
          <div className="stk-filing-note" data-testid="grievance-deadline-note">
            {draft.deadline_note}
          </div>
        )}

        {/* Expandable full template */}
        <button
          className="stk-expand-btn"
          onClick={() => setExpanded(!expanded)}
          data-testid="expand-template-btn"
        >
          {expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
          <span>{expanded ? 'Hide Full Template' : 'View Full Template'}</span>
        </button>

        {expanded && (
          <pre className="stk-template-text" data-testid="grievance-template-text">
            {draft.template_text}
          </pre>
        )}

        <CopyButton
          text={draft.template_text}
          label="Copy Draft Grievance"
          testId="copy-grievance-btn"
        />
      </div>
    </div>
  );
}

/* -- Steward Toolkit Panel -- */

/* -- P81: Sources Panel (User-Facing) ------------------------------------
   Shows which documents AskStewie used to compose the answer.
   - One entry per book (deduped from chunk-level retrieval)
   - Category chip (CBA / Memorandum / Procedure / Document)
   - Top match snippet (expandable)
   - Sections cited (Article / Section labels, when present)
   - "View original" button — fetches /api/book/download/{book_id} with
     the user's auth token and opens the PDF in a new tab.
--------------------------------------------------------------------------- */

const CATEGORY_LABELS = {
  contract: { label: 'CBA', color: '#10b981', bg: 'rgba(16,185,129,0.12)', border: 'rgba(16,185,129,0.4)' },
  memorandum: { label: 'MOA / Letter', color: '#a78bfa', bg: 'rgba(167,139,250,0.12)', border: 'rgba(167,139,250,0.4)' },
  procedure: { label: 'Procedure', color: '#60a5fa', bg: 'rgba(96,165,250,0.12)', border: 'rgba(96,165,250,0.4)' },
  policy: { label: 'Policy', color: '#fbbf24', bg: 'rgba(251,191,36,0.12)', border: 'rgba(251,191,36,0.4)' },
  document: { label: 'Document', color: '#a1a1aa', bg: 'rgba(255,255,255,0.04)', border: 'rgba(255,255,255,0.12)' },
};

function SourceItem({ src, index }) {
  const [expanded, setExpanded] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const cat = CATEGORY_LABELS[src.category] || CATEGORY_LABELS.document;

  async function viewOriginal() {
    try {
      setDownloading(true);
      const apiBase = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token') || localStorage.getItem('access_token') || '';
      const res = await fetch(`${apiBase}/api/book/download/${src.book_id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      // Defer revoke so the new tab has time to load
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      console.warn('Source download failed:', err);
    }
    setDownloading(false);
  }

  return (
    <div className="sa-source-item" data-testid={`source-item-${index}`}>
      <div className="sa-source-row">
        <span
          className="sa-source-chip"
          style={{ color: cat.color, background: cat.bg, borderColor: cat.border }}
          data-testid={`source-chip-${index}`}
        >
          {cat.label}
        </span>
        <span className="sa-source-title" data-testid={`source-title-${index}`}>{src.source_title}</span>
        <span className="sa-source-score" title="Relevance score">{(src.top_score * 100).toFixed(0)}%</span>
      </div>

      {src.sections && src.sections.length > 0 && (
        <div className="sa-source-sections" data-testid={`source-sections-${index}`}>
          {src.sections.map((s, i) => (
            <span key={i} className="sa-source-section-pill">{s}</span>
          ))}
        </div>
      )}

      <div className="sa-source-actions">
        <button
          type="button"
          className="sa-source-toggle"
          onClick={() => setExpanded(e => !e)}
          data-testid={`source-toggle-${index}`}
        >
          {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          {expanded ? 'Hide snippet' : 'Show what was cited'}
          <span className="sa-source-chunks">· {src.chunk_count} {src.chunk_count === 1 ? 'chunk' : 'chunks'}</span>
        </button>
        {src.downloadable && (
          <button
            type="button"
            className="sa-source-download"
            onClick={viewOriginal}
            disabled={downloading}
            data-testid={`source-download-${index}`}
          >
            <Download size={12} />
            {downloading ? 'Opening…' : 'View original'}
          </button>
        )}
      </div>

      {expanded && (
        <div className="sa-source-snippet" data-testid={`source-snippet-${index}`}>
          “{src.snippet}”
        </div>
      )}
    </div>
  );
}

function SourcesPanel({ sources }) {
  const [open, setOpen] = useState(true);
  if (!Array.isArray(sources) || sources.length === 0) return null;

  return (
    <div className="sa-sources-panel" data-testid="sources-panel">
      <button
        type="button"
        className="sa-sources-head"
        onClick={() => setOpen(o => !o)}
        data-testid="sources-toggle"
      >
        <BookMarked size={13} />
        <span className="sa-sources-title">SOURCES USED</span>
        <span className="sa-sources-count">{sources.length} {sources.length === 1 ? 'document' : 'documents'}</span>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </button>
      {open && (
        <div className="sa-sources-body" data-testid="sources-list">
          {sources.map((s, i) => (
            <SourceItem key={s.book_id || i} src={s} index={i} />
          ))}
          <div className="sa-sources-footnote">
            AskStewie composed this answer using only the documents above. No outside knowledge.
          </div>
        </div>
      )}
    </div>
  );
}

function StewardToolkitPanel({ toolkit }) {
  if (!toolkit) return null;
  const hasWeingarten = toolkit.show_weingarten_card === true && toolkit.weingarten_card;
  const hasGrievance = !!toolkit.grievance_draft;
  if (!hasWeingarten && !hasGrievance) return null;

  return (
    <div className="stk-panel" data-testid="steward-toolkit-panel">
      <div className="stk-panel-head">
        <Shield size={14} />
        <span>STEWARD TOOLKIT</span>
      </div>
      <div className="stk-panel-body">
        {hasWeingarten && <WeingartenCard card={toolkit.weingarten_card} />}
        {hasGrievance && <GrievanceDraftCard draft={toolkit.grievance_draft} />}
      </div>
    </div>
  );
}

/* -- Follow-Up Pills (Intelligent Suggestions) -- */

function FollowUpPills({ follow, onPick }) {
  if (!follow || !follow.show) return null;
  const questions = Array.isArray(follow.questions) ? follow.questions.slice(0, 4) : [];
  const questionIds = Array.isArray(follow.question_ids) ? follow.question_ids : [];
  if (!questions.length) return null;

  return (
    <div className="sa-followup-pills" data-testid="follow-up-pills-section">
      <div className="sa-followup-pills-head">
        <MessageCircle size={13} />
        <span>{follow.heading || 'Helpful follow-up questions:'}</span>
      </div>
      <div className="sa-followup-pills-body" data-testid="follow-up-pills-list">
        {questions.map((q, i) => {
          const pillId = questionIds[i] || null;
          return (
            <button
              key={pillId || `pill-${i}`}
              type="button"
              className="sa-followup-pill"
              data-testid={`follow-up-pill-${i}`}
              data-pill-id={pillId || ''}
              onClick={() => onPick && onPick(q, i, pillId)}
            >
              <span className="sa-followup-pill-text">{q}</span>
              <ChevronRight size={13} className="sa-followup-pill-arrow" />
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* -- Hot Pills (Social Proof: "Other stewards are also asking") -- */
function HotPillsOtherStewards({ pills, onPick }) {
  // Backend already deduped against the main follow-up pills and capped at 3.
  if (!Array.isArray(pills) || pills.length === 0) return null;
  return (
    <div className="sa-hot-pills" data-testid="hot-pills-section">
      <div className="sa-hot-pills-head">
        Other stewards are also asking:
      </div>
      <div className="sa-hot-pills-body" data-testid="hot-pills-list">
        {pills.map((p, i) => (
          <button
            key={p.pill_id || `hot-${i}`}
            type="button"
            className="sa-hot-pill"
            data-testid={`hot-pill-${i}`}
            data-pill-id={p.pill_id || ''}
            onClick={() => onPick && onPick(p.pill_text, i, p.pill_id)}
            title={`Clicked ${p.count}x in the last 7 days`}
          >
            <span className="sa-hot-pill-text">{p.pill_text}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export {
  Badge,
  Section,
  DecisionLogPanel,
  CopyButton,
  WeingartenCard,
  GrievanceDraftCard,
  SourcesPanel,
  StewardToolkitPanel,
  FollowUpPills,
  HotPillsOtherStewards,
};
